import { FlowStatuses } from '../constants'
import { clearLocalDataByPrefix, getLocalData, saveLocalData } from './storage'
import { FomToken } from './FomToken'
import { getHigchartAsPng } from './scoringDimensionsService'
import WebPlayActionCardService from './WebPlayActionCardService'
import { getActionCardModule } from '../actionCardModule'
const { FileQuestionNode } = await getActionCardModule()

const FOM_SERVICE_URL = import.meta.env.VITE_FOM_SERVICE_URL

const fomService = {
  async getWebPlayInfo(playbookId) {
    const infoResponse = await fetch(`${FOM_SERVICE_URL}/getWebPlayInfo?playbookId=${playbookId}`)
    return await infoResponse.json()
  },

  async createPlay(playbookId, title, keywords, score, scoringDimensions, lastActiveSeparator, flow, loggedInUserId) {
    const url = new URL(`${FOM_SERVICE_URL}/createPlay`)
    url.searchParams.append('playbookId', playbookId)
    url.searchParams.append('title', title)
    url.searchParams.append('keywords', keywords)
    url.searchParams.append('score', score)
    url.searchParams.append('lastActiveSeparator', lastActiveSeparator || '')
    url.searchParams.append('flow', FlowStatuses[flow] || FlowStatuses.play)
    url.searchParams.append('loggedInUserId', loggedInUserId)

    const formData = new FormData()
    try {
      const scoringDimensionsString = scoringDimensions?.length > 0 ? JSON.stringify(scoringDimensions) : null
      if (scoringDimensionsString) {
        formData.append('scoringDimensions', scoringDimensionsString)
      }
    } catch {}

    let playCreationResponse = await fetch(url, {
      method: 'POST',
      body: formData,
    })
    if (playCreationResponse.status !== 200) {
      throw new Error('Failed to create play: ' + playCreationResponse.statusText)
    }
    const fomToken = new FomToken(playCreationResponse)
    const playId = await playCreationResponse.text()

    return { playId, fomToken }
  },

  async attachFileToPlay(playbookId, playId, headerId, headerName, file, fomToken) {
    if (file.size > 30 * 1024 * 1024) {
      throw new Error('File size exceeds the limit of 30MB')
    }

    const url = new URL(`${FOM_SERVICE_URL}/attachFileToPlay`)
    url.searchParams.append('playbookId', playbookId)
    url.searchParams.append('playId', playId)
    url.searchParams.append('headerId', headerId)
    url.searchParams.append('headerName', headerName)

    const formData = new FormData()
    formData.append('file', file)

    const headers = fomToken.appendToObject({})

    const response = await fetch(url, {
      method: 'POST',
      body: formData,
      headers: headers,
    })

    if (!response.ok) {
      console.error('Failed to attach file to project file:', response.statusText)
      throw new Error('Server error status ' + response.status)
    }

    const fileData = await response.json()
    return [fileData.id, fileData.url]
  },

  async attachActionCardToPlay(playbookId, playId, actionCard, fomToken) {
    const url = new URL(`${FOM_SERVICE_URL}/attachActionCardToPlay`)
    url.searchParams.append('playbookId', playbookId)
    url.searchParams.append('playId', playId)

    const headers = fomToken.appendToObject({})
    headers['Content-Type'] = 'application/json'

    const response = await fetch(url, {
      method: 'POST',
      headers: headers,
      body: JSON.stringify(actionCard),
    })

    if (!response.ok) {
      console.error('Failed to attach action card to play:', response.statusText)
      throw new Error('Server error status ' + response.status)
    }
  },

  async sendPlayCreatedNotification(playbookId, playId, email, keywords, actionCardModel, fomToken) {
    const url = new URL(`${FOM_SERVICE_URL}/sendPlayCreatedNotification`)
    url.searchParams.append('playbookId', playbookId)
    url.searchParams.append('playId', playId)

    const headers = fomToken.appendToObject({})

    const formData = new FormData()
    formData.append('email', email)
    formData.append('keywords', keywords)
    formData.append('actionCardData', getActionCardDataReplacementInfo(actionCardModel))

    const pngBlob = await getHigchartAsPng(actionCardModel.__actionCardData)
    if (pngBlob) {
      formData.append('scoringDimensionsChart', pngBlob)
    }

    const response = await fetch(url, {
      method: 'POST',
      headers: headers,
      body: formData,
    })

    if (!response.ok) {
      console.error('Failed to attach action card to play:', response.statusText)
      throw new Error('Server error status ' + response.status)
    }
  },

  async createAnonymousToken() {
    const response = await fetch(`${FOM_SERVICE_URL}/createAnonymousToken`)
    return await response.text()
  },

  async getActionCard({ playbookId, actionCardUrl, headersCount, email }) {
    let localData = getLocalData(playbookId)

    if (localData) {
      return localData
    }
    if (actionCardUrl) {
      const response = await fetch(actionCardUrl)
      const data = await response.json()

      const actionCardService = new WebPlayActionCardService(data)

      actionCardService.adjustEmail(email)
      actionCardService.adjustModelFromQueryString()

      if (headersCount === 1 && data.sections.length && data.sections[0].type === 'Separator') {
        // expand single separator if it comes first as it will be hidden
        data.sections[0].isOpened = true
      }

      clearLocalDataByPrefix(playbookId)
      saveLocalData(data, playbookId)

      return data
    } else {
      throw new Error('No action card found for playbookId: ' + playbookId)
    }
  },

  async getPlaybookId(code) {
    const infoResponse = await fetch(`${FOM_SERVICE_URL}/getWebPlayIdByCode?code=${code}`)
    return await infoResponse.json()
  },

  async rollbackPlayCreation(playbookId, playId, fomToken) {
    try {
      const url = new URL(`${FOM_SERVICE_URL}/RollbackPlayCreation`)
      url.searchParams.append('playbookId', playbookId)
      url.searchParams.append('playId', playId)

      const headers = fomToken.appendToObject({})

      const response = await fetch(url, {
        method: 'POST',
        headers: headers,
      })

      if (!response.ok) {
        console.error('Failed to rollback play creation:', response.statusText)
      }
    } catch (ex) {
      console.error('Failed to rollback play creation', ex)
    }
  },
}

function getActionCardDataReplacementInfo(actionCardModel) {
  const valueFields = actionCardModel.getSearchService().filterVisible().filterNotEmptyInputNodes()

  const rows = []

  for (const valueField of valueFields) {
    if (valueField instanceof FileQuestionNode) {
      let fileQuestionHtmlValue = ''
      for (const fileItem of valueField.data.value) {
        if (fileItem.id > 0 && !fileItem.error) {
          const fileHtmlValue = `<a href="${fileItem.url}" target="_blank">${fileItem.name}</a>`
          fileQuestionHtmlValue += fileQuestionHtmlValue ? '; ' + fileHtmlValue : fileHtmlValue
        }
      }
      rows.push(`<tr><td>${valueField.getLabel()}: </td><td>${fileQuestionHtmlValue}</td></tr>`)
      continue
    }

    let value = valueField.stringifyValue().replaceAll('\n', '<br/>')
    rows.push(`<tr><td>${valueField.getLabel()}: </td><td>${value}</td></tr>`)
  }

  if (!rows.length) {
    return ''
  }

  return `<table style="border-spacing: 2px 4px;"><tbody style="vertical-align: top;">${rows.join(
    '\n',
  )}</tbody></table>`
}

export default fomService
